import { format, parseISO } from 'date-fns';

import {
  formatCompareDelta,
  formatFinanceValue,
  granularityLabel,
  type FinanceMetricFormat,
} from './formatters';
import {
  buildFinanceAdditiveTotals,
  resolveFinanceRowTotal,
} from './financeTotals';
import type { FinanceForecastRow } from './ForecastTable';

/**
 * Soft ceiling per landscape page — prefer pagination over cramming.
 * (~6 months per page stays readable and intentional.)
 */
export const FINANCE_PDF_MAX_PERIOD_COLS = 6;
const MIN_VALUE_COL_W = 92;
const METRIC_COL_W = 148;

const MARGIN = 36;
const BRAND_BAR = [30, 237, 159] as const; // Kipify primary
const BRAND_SOFT = [232, 251, 243] as const; // mint wash under header
const BRAND_SOFT_EDGE = [201, 255, 235] as const; // primary.light
const KPI = [1, 128, 148] as const; // readable teal for KPI on white print
const INK = [48, 54, 66] as const;
const MUTED = [104, 114, 133] as const;
const LINE = [220, 224, 232] as const;
const SECTION_BG = [238, 242, 247] as const;
const SUBTOTAL_BG = [244, 251, 248] as const;
const NEGATIVE = [198, 40, 40] as const;
const DELTA_POS = [21, 128, 61] as const;
const VALUE_RIGHT_PAD = 12;

export type FinancePdfInput = {
  periods: string[];
  rows: FinanceForecastRow[];
  granularity?: string;
  dateRangeLabel: string;
  compareRangeLabel?: string | null;
  compareActive?: boolean;
  costSummary?: string | null;
  generatedAt?: Date;
};

type JsPdfInstance = {
  internal: { pageSize: { getWidth: () => number; getHeight: () => number } };
  setFillColor: (...args: number[]) => void;
  setDrawColor: (...args: number[]) => void;
  setTextColor: (...args: number[]) => void;
  setFont: (family: string, style?: string) => void;
  setFontSize: (size: number) => void;
  setLineWidth: (width: number) => void;
  text: (
    text: string | string[],
    x: number,
    y: number,
    options?: { align?: 'left' | 'center' | 'right'; maxWidth?: number },
  ) => void;
  rect: (x: number, y: number, w: number, h: number, style?: string) => void;
  line: (x1: number, y1: number, x2: number, y2: number) => void;
  addPage: () => void;
  getNumberOfPages: () => number;
  setPage: (page: number) => void;
  save: (filename: string) => void;
  splitTextToSize: (text: string, maxWidth: number) => string[];
  getTextWidth: (text: string) => number;
};

type JsPdfCtor = new (options: {
  unit: string;
  format: string;
  orientation: string;
}) => JsPdfInstance;

function loadScript(src: string, globalKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const win = window as unknown as {
      jspdf?: { jsPDF?: JsPdfCtor };
      jsPDF?: JsPdfCtor;
    };
    if (win.jspdf?.jsPDF || win.jsPDF) {
      resolve();
      return;
    }
    const existing = document.querySelector(
      `script[data-kipify-pdf="${globalKey}"]`,
    );
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () =>
        reject(new Error(`Failed to load ${globalKey}`)),
      );
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.kipifyPdf = globalKey;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${globalKey}`));
    document.head.appendChild(script);
  });
}

export async function ensureFinancePdfEngine(): Promise<JsPdfCtor> {
  const win = window as unknown as {
    jspdf?: { jsPDF?: JsPdfCtor };
    jsPDF?: JsPdfCtor;
  };
  if (!win.jspdf?.jsPDF && !win.jsPDF) {
    await loadScript(
      'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js',
      'jspdf',
    );
  }
  const Ctor = win.jspdf?.jsPDF ?? win.jsPDF;
  if (!Ctor) {
    throw new Error('jsPDF non disponibile dopo il caricamento dello script.');
  }
  return Ctor;
}

/** Compact period headers so more columns fit on one landscape page. */
export function formatFinancePdfPeriodLabel(
  period: string,
  granularity?: string,
): string {
  if (!period) return period;

  if (granularity === 'year' || /^\d{4}$/.test(period)) {
    return period.slice(0, 4);
  }

  if (granularity === 'month' || /^\d{4}-\d{2}$/.test(period)) {
    try {
      return format(parseISO(`${period}-01T12:00:00`), 'MMM yy');
    } catch {
      return period;
    }
  }

  try {
    const dateStr = period.length <= 10 ? `${period}T12:00:00` : period;
    return format(parseISO(dateStr), 'd MMM');
  } catch {
    return period;
  }
}

/** Split period columns into page chunks using available landscape width. */
export function chunkPeriodIndexes(
  periodCount: number,
  maxCols = FINANCE_PDF_MAX_PERIOD_COLS,
): Array<{ start: number; end: number }> {
  if (periodCount <= 0) return [];
  const safeMax = Math.max(1, maxCols);
  const chunks: Array<{ start: number; end: number }> = [];
  for (let start = 0; start < periodCount; start += safeMax) {
    chunks.push({ start, end: Math.min(start + safeMax, periodCount) });
  }
  return chunks;
}

/** How many period columns fit on one landscape A4 page. */
export function resolvePdfPeriodColsPerPage(
  periodCount: number,
  pageWidthPt = 841.89,
): number {
  const usable = pageWidthPt - MARGIN * 2 - METRIC_COL_W;
  const byWidth = Math.max(1, Math.floor(usable / MIN_VALUE_COL_W));
  return Math.min(periodCount, Math.min(FINANCE_PDF_MAX_PERIOD_COLS, byWidth));
}

export function resolvePeriodChunks(
  periodCount: number,
  pageWidthPt = 841.89,
): Array<{ start: number; end: number }> {
  return chunkPeriodIndexes(
    periodCount,
    resolvePdfPeriodColsPerPage(periodCount, pageWidthPt),
  );
}

export function buildFinancePdfFilename(
  dateRangeLabel: string,
  generatedAt = new Date(),
): string {
  const stamp = generatedAt.toISOString().slice(0, 10);
  const rangeSlug = dateRangeLabel
    .replace(/[–—]/g, '-')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);
  return `kipify-finance-pnl-${rangeSlug || 'report'}-${stamp}.pdf`;
}

/**
 * PDF currency parts: amount without symbol so we can draw `€` + gap + digits
 * (Helvetica collapses a plain space after €).
 */
export function formatFinancePdfCurrencyParts(
  value: number | null | undefined,
): { negative: boolean; amount: string } | null {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return null;
  }
  return {
    negative: value < 0,
    amount: new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.abs(value)),
  };
}

export function formatFinancePdfValue(
  value: number | null | undefined,
  format: FinanceMetricFormat,
): string {
  if (format !== 'currency') {
    return formatFinanceValue(value, format);
  }
  const parts = formatFinancePdfCurrencyParts(value);
  if (!parts) return '—';
  // Keep string form for tests / fallbacks; draw path uses parts + gap.
  return parts.negative ? `(€  ${parts.amount})` : `€  ${parts.amount}`;
}

const CURRENCY_GAP_PT = 4;

function drawPdfCellValue(
  pdf: JsPdfInstance,
  value: number,
  format: FinanceMetricFormat,
  x: number,
  y: number,
): void {
  if (format !== 'currency') {
    pdf.text(formatFinancePdfValue(value, format), x, y, { align: 'right' });
    return;
  }
  const parts = formatFinancePdfCurrencyParts(value);
  if (!parts) {
    pdf.text('—', x, y, { align: 'right' });
    return;
  }

  let cursor = x;
  if (parts.negative) {
    pdf.text(')', cursor, y, { align: 'right' });
    cursor -= pdf.getTextWidth(')');
  }
  pdf.text(parts.amount, cursor, y, { align: 'right' });
  cursor -= pdf.getTextWidth(parts.amount) + CURRENCY_GAP_PT;
  pdf.text('€', cursor, y, { align: 'right' });
  if (parts.negative) {
    cursor -= pdf.getTextWidth('€');
    pdf.text('(', cursor, y, { align: 'right' });
  }
}

function rgb(
  tuple: readonly [number, number, number],
): [number, number, number] {
  return [tuple[0], tuple[1], tuple[2]];
}

function drawFooter(
  pdf: JsPdfInstance,
  pageW: number,
  pageH: number,
  page: number,
  total: number,
  generatedAt: Date,
) {
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(...rgb(MUTED));
  pdf.text('Kipify · Store P&L', MARGIN, pageH - 16);
  pdf.text(
    `Generated ${generatedAt.toLocaleString('en-GB')}`,
    pageW / 2,
    pageH - 16,
    {
      align: 'center',
    },
  );
  pdf.text(`Page ${page} of ${total}`, pageW - MARGIN, pageH - 16, {
    align: 'right',
  });
}

function drawHeader(
  pdf: JsPdfInstance,
  pageW: number,
  input: FinancePdfInput,
  chunkLabel: string | null,
): number {
  const metaParts = [
    input.dateRangeLabel,
    input.granularity ? granularityLabel(input.granularity) : null,
    input.compareRangeLabel ? `vs ${input.compareRangeLabel}` : null,
    chunkLabel,
  ].filter(Boolean) as string[];
  const metaLine = metaParts.join('  ·  ');
  const costLines = input.costSummary
    ? pdf.splitTextToSize(
        `Additional costs: ${input.costSummary}`,
        pageW - MARGIN * 2 - 16,
      )
    : [];

  // Header band: brand + title + meta strip + optional costs
  const headerBandH =
    78 + (costLines.length > 0 ? 6 + costLines.length * 10 : 0);

  pdf.setFillColor(...rgb(BRAND_SOFT));
  pdf.rect(0, 0, pageW, headerBandH, 'F');
  pdf.setFillColor(...rgb(BRAND_BAR));
  pdf.rect(0, 0, pageW, 5, 'F');
  pdf.setFillColor(...rgb(BRAND_SOFT_EDGE));
  pdf.rect(0, headerBandH - 2, pageW, 2, 'F');

  // Brand mark + wordmark
  let y = 18;
  pdf.setFillColor(...rgb(BRAND_BAR));
  pdf.rect(MARGIN, y, 10, 10, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(...rgb(INK));
  pdf.text('KIPIFY', MARGIN + 16, y + 9);

  // FINANCE pill
  const pillX = MARGIN + 72;
  const pillW = 58;
  const pillH = 14;
  pdf.setFillColor(255, 255, 255);
  pdf.setDrawColor(...rgb(BRAND_BAR));
  pdf.setLineWidth(0.8);
  pdf.rect(pillX, y - 1, pillW, pillH, 'FD');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(7.5);
  pdf.setTextColor(...rgb(KPI));
  pdf.text('FINANCE', pillX + pillW / 2, y + 8.5, { align: 'center' });

  // Title + accent underline
  y = 44;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(20);
  pdf.setTextColor(...rgb(INK));
  pdf.text('Store P&L', MARGIN, y);
  pdf.setFillColor(...rgb(BRAND_BAR));
  pdf.rect(MARGIN, y + 5, 52, 3, 'F');

  // Meta strip
  y = 62;
  const stripH = 16 + (costLines.length > 0 ? 4 + costLines.length * 10 : 0);
  pdf.setFillColor(255, 255, 255);
  pdf.setDrawColor(...rgb(LINE));
  pdf.setLineWidth(0.5);
  pdf.rect(MARGIN, y, pageW - MARGIN * 2, stripH, 'FD');

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8.5);
  pdf.setTextColor(...rgb(MUTED));
  pdf.text(metaLine, MARGIN + 8, y + 11);

  if (costLines.length > 0) {
    pdf.setFontSize(8);
    pdf.text(costLines, MARGIN + 8, y + 22);
  }

  return headerBandH + 14;
}

type ColLayout = {
  metricW: number;
  valueW: number;
};

function layoutColumns(
  pageW: number,
  periodCols: number,
  includeTotal = true,
): ColLayout {
  const usable = pageW - MARGIN * 2;
  const metricW = METRIC_COL_W;
  const valueCols = Math.max(1, periodCols + (includeTotal ? 1 : 0));
  const valueW = (usable - metricW) / valueCols;
  return { metricW, valueW };
}

function drawTableHeader(
  pdf: JsPdfInstance,
  y: number,
  pageW: number,
  layout: ColLayout,
  periods: string[],
  granularity?: string,
  includeTotal = true,
): number {
  const rowH = 18;
  pdf.setFillColor(248, 250, 252);
  pdf.rect(MARGIN, y, pageW - MARGIN * 2, rowH, 'F');

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  pdf.setTextColor(...rgb(MUTED));
  pdf.text('Metric', MARGIN + 4, y + 12);

  periods.forEach((period, index) => {
    const x =
      MARGIN + layout.metricW + (index + 1) * layout.valueW - VALUE_RIGHT_PAD;
    const label = formatFinancePdfPeriodLabel(period, granularity);
    pdf.text(label, x, y + 12, {
      align: 'right',
      maxWidth: layout.valueW - VALUE_RIGHT_PAD - 2,
    });
  });

  if (includeTotal) {
    const x =
      MARGIN +
      layout.metricW +
      (periods.length + 1) * layout.valueW -
      VALUE_RIGHT_PAD;
    pdf.setTextColor(...rgb(INK));
    pdf.text('Total', x, y + 12, { align: 'right' });
  }

  pdf.setDrawColor(...rgb(LINE));
  pdf.setLineWidth(0.5);
  pdf.line(MARGIN, y + rowH, pageW - MARGIN, y + rowH);
  return y + rowH + 2;
}

function ensureSpace(
  pdf: JsPdfInstance,
  y: number,
  needed: number,
  pageH: number,
  pageW: number,
  input: FinancePdfInput,
  chunkLabel: string | null,
  layout: ColLayout,
  periodsSlice: string[],
): number {
  if (y + needed <= pageH - 28) return y;
  pdf.addPage();
  let nextY = drawHeader(pdf, pageW, input, chunkLabel);
  nextY = drawTableHeader(
    pdf,
    nextY,
    pageW,
    layout,
    periodsSlice,
    input.granularity,
    true,
  );
  return nextY;
}

function drawDataRows(
  pdf: JsPdfInstance,
  startY: number,
  pageW: number,
  pageH: number,
  input: FinancePdfInput,
  chunkLabel: string | null,
  layout: ColLayout,
  periodStart: number,
  periodEnd: number,
): void {
  const periodsSlice = input.periods.slice(periodStart, periodEnd);
  let y = startY;
  const compareActive = Boolean(input.compareActive);
  const additiveTotals = buildFinanceAdditiveTotals(input.rows, 'values');
  const compareAdditiveTotals = compareActive
    ? buildFinanceAdditiveTotals(input.rows, 'compareValues')
    : null;

  for (const row of input.rows) {
    if (row.emphasis === 'spacer') {
      y = ensureSpace(
        pdf,
        y,
        10,
        pageH,
        pageW,
        input,
        chunkLabel,
        layout,
        periodsSlice,
      );
      y += 8;
      continue;
    }

    if (row.emphasis === 'section') {
      const rowH = 20;
      y = ensureSpace(
        pdf,
        y,
        rowH + 2,
        pageH,
        pageW,
        input,
        chunkLabel,
        layout,
        periodsSlice,
      );
      pdf.setFillColor(...rgb(SECTION_BG));
      pdf.rect(MARGIN, y, pageW - MARGIN * 2, rowH, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(...rgb(MUTED));
      pdf.text(row.label.toUpperCase(), MARGIN + 4, y + 13);
      y += rowH;
      continue;
    }

    const hasDelta = compareActive && Boolean(row.compareValues?.length);
    const rowH = hasDelta ? 22 : 16;
    y = ensureSpace(
      pdf,
      y,
      rowH + 1,
      pageH,
      pageW,
      input,
      chunkLabel,
      layout,
      periodsSlice,
    );

    if (row.emphasis === 'subtotal') {
      pdf.setFillColor(...rgb(SUBTOTAL_BG));
      pdf.rect(MARGIN, y, pageW - MARGIN * 2, rowH, 'F');
    }

    const isKpi = row.emphasis === 'kpi';
    pdf.setFont(
      'helvetica',
      row.emphasis === 'subtotal' || isKpi ? 'bold' : 'normal',
    );
    pdf.setFontSize(8);
    pdf.setTextColor(...rgb(isKpi ? KPI : INK));

    const metricLines = pdf.splitTextToSize(row.label, layout.metricW - 8);
    pdf.text(metricLines[0] ?? row.label, MARGIN + 4, y + (hasDelta ? 9 : 11));

    for (let i = periodStart; i < periodEnd; i += 1) {
      const localIndex = i - periodStart;
      const value = row.values[i] ?? 0;
      const x =
        MARGIN +
        layout.metricW +
        (localIndex + 1) * layout.valueW -
        VALUE_RIGHT_PAD;

      pdf.setFont(
        'helvetica',
        row.emphasis === 'subtotal' || isKpi ? 'bold' : 'normal',
      );
      pdf.setFontSize(8.5);
      if (value < 0) {
        pdf.setTextColor(...rgb(NEGATIVE));
      } else if (isKpi) {
        pdf.setTextColor(...rgb(KPI));
      } else {
        pdf.setTextColor(...rgb(INK));
      }
      drawPdfCellValue(
        pdf,
        value,
        row.format as FinanceMetricFormat,
        x,
        y + (hasDelta ? 9 : 11),
      );

      if (hasDelta) {
        const compareValue = row.compareValues?.[i] ?? null;
        const delta =
          compareValue !== null
            ? formatCompareDelta(
                value,
                compareValue,
                row.format as FinanceMetricFormat,
              )
            : null;
        if (delta) {
          const deltaNegative = delta.startsWith('-');
          const deltaPositive =
            !deltaNegative &&
            delta !== 'New' &&
            !delta.startsWith('+0') &&
            delta !== '0.0%';
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(7);
          pdf.setTextColor(
            ...(deltaNegative ? NEGATIVE : deltaPositive ? DELTA_POS : MUTED),
          );
          pdf.text(delta, x, y + 18, { align: 'right' });
        }
      }
    }

    // Period-range Total (all periods, not just this chunk)
    const totalValue = resolveFinanceRowTotal(row, additiveTotals);
    const totalX =
      MARGIN +
      layout.metricW +
      (periodsSlice.length + 1) * layout.valueW -
      VALUE_RIGHT_PAD;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8.5);
    if (totalValue === null) {
      pdf.setTextColor(...rgb(MUTED));
      pdf.text('—', totalX, y + (hasDelta ? 9 : 11), { align: 'right' });
    } else {
      if (totalValue < 0) {
        pdf.setTextColor(...rgb(NEGATIVE));
      } else if (isKpi) {
        pdf.setTextColor(...rgb(KPI));
      } else {
        pdf.setTextColor(...rgb(INK));
      }
      drawPdfCellValue(
        pdf,
        totalValue,
        row.format as FinanceMetricFormat,
        totalX,
        y + (hasDelta ? 9 : 11),
      );
      if (hasDelta && compareAdditiveTotals) {
        const compareTotal = resolveFinanceRowTotal(row, compareAdditiveTotals);
        const totalDelta =
          compareTotal !== null
            ? formatCompareDelta(
                totalValue,
                compareTotal,
                row.format as FinanceMetricFormat,
              )
            : null;
        if (totalDelta) {
          const deltaNegative = totalDelta.startsWith('-');
          const deltaPositive =
            !deltaNegative &&
            totalDelta !== 'New' &&
            !totalDelta.startsWith('+0') &&
            totalDelta !== '0.0%';
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(7);
          pdf.setTextColor(
            ...(deltaNegative ? NEGATIVE : deltaPositive ? DELTA_POS : MUTED),
          );
          pdf.text(totalDelta, totalX, y + 18, { align: 'right' });
        }
      }
    }

    pdf.setDrawColor(...rgb(LINE));
    pdf.setLineWidth(0.3);
    pdf.line(MARGIN, y + rowH, pageW - MARGIN, y + rowH);
    y += rowH;
  }
}

export async function downloadFinancePdf(
  input: FinancePdfInput,
): Promise<void> {
  if (!input.periods.length || !input.rows.length) {
    throw new Error('Nessun dato finance da esportare.');
  }

  const JsPDF = await ensureFinancePdfEngine();
  const pdf = new JsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const generatedAt = input.generatedAt ?? new Date();

  const chunks = resolvePeriodChunks(input.periods.length, pageW);
  chunks.forEach((chunk, chunkIndex) => {
    if (chunkIndex > 0) pdf.addPage();

    const periodsSlice = input.periods.slice(chunk.start, chunk.end);
    const chunkLabel =
      chunks.length > 1
        ? `Periods ${chunk.start + 1}–${chunk.end} of ${input.periods.length} · continued on next page(s)`
        : null;
    const layout = layoutColumns(pageW, periodsSlice.length, true);

    let y = drawHeader(pdf, pageW, input, chunkLabel);
    y = drawTableHeader(
      pdf,
      y,
      pageW,
      layout,
      periodsSlice,
      input.granularity,
      true,
    );
    drawDataRows(
      pdf,
      y,
      pageW,
      pageH,
      input,
      chunkLabel,
      layout,
      chunk.start,
      chunk.end,
    );
  });

  const totalPages = pdf.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    pdf.setPage(page);
    drawFooter(pdf, pageW, pageH, page, totalPages, generatedAt);
  }

  pdf.save(buildFinancePdfFilename(input.dateRangeLabel, generatedAt));
}
